import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import SBINModal from "./SBINModal";
import {
  createTicketRefund,
  getRefundContext,
  getRefundPreview,
  updateTicketRefund,
} from "../../api/tickets";
import {
  checkGiftCardCodeAvailable,
  generateGiftCardCode,
} from "../../api/giftCards";

const REASON_OPTIONS = [
  "Service issue / redo",
  "Customer cancellation",
  "Pricing error",
  "Duplicate charge",
  "Goodwill",
  "Other",
];

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

function formatMethodLabel(method) {
  const normalized = String(method || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  const labelMap = {
    cash: "Cash",
    debit: "Debit",
    credit: "Credit",
    gift_card: "Gift Card",
  };
  return labelMap[normalized] || "Other";
}

function getPaymentKey(payment) {
  if (payment?.key) return payment.key;
  const method = payment?.method || "";
  const reference = payment?.reference || "";
  return `${method}:${reference}`;
}

function parseAmount(value) {
  if (value == null || value === "") return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function getGiftCardLabel(line) {
  const code = String(line?.gift_card_code || "").trim();
  if (code) return code;
  const description = String(line?.description || "").trim();
  const match = description.match(/gift card:\s*(.+)$/i);
  if (match && match[1]) return match[1].trim();
  return description || "--";
}

export default function RefundTicketModal({
  isOpen,
  ticketId,
  mode = "create",
  originalTicketId = null,
  refundTicketId = null,
  initialContext = null,
  onClose,
  onRefundCreated,
  onRefundUpdated,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [lineAmounts, setLineAmounts] = useState({});
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [refundMode, setRefundMode] = useState("original");
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardWarning, setGiftCardWarning] = useState("");
  const [giftCardChecking, setGiftCardChecking] = useState(false);
  const [giftCardSuggested, setGiftCardSuggested] = useState(false);
  const [showIssuedGiftCard, setShowIssuedGiftCard] = useState(false);
  const [issuedGiftCardCode, setIssuedGiftCardCode] = useState("");
  const [allocationMode, setAllocationMode] = useState("auto");
  const [allocationsByKey, setAllocationsByKey] = useState({});
  const [allocationDrafts, setAllocationDrafts] = useState({});
  const [focusedAllocationKey, setFocusedAllocationKey] = useState(null);
  const [computedAutoAllocations, setComputedAutoAllocations] = useState({});
  const [autoAllocationError, setAutoAllocationError] = useState("");
  const [manualAllocationError, setManualAllocationError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;
    setLoading(true);
    setError("");
    setPreview(null);
    setLineAmounts({});
    setReason("");
    setNote("");
    setRefundMode("original");
    setGiftCardCode("");
    setGiftCardWarning("");
    setGiftCardChecking(false);
    setGiftCardSuggested(false);
    setShowIssuedGiftCard(false);
    setIssuedGiftCardCode("");
    setAllocationMode("auto");
    setAllocationsByKey({});
    setAllocationDrafts({});
    setComputedAutoAllocations({});
    setAutoAllocationError("");
    setManualAllocationError("");

    const loadRefundData = async () => {
      try {
        if (mode === "edit") {
          const context =
            initialContext ||
            (refundTicketId ? await getRefundContext(refundTicketId) : null);
          if (!isActive) return;
          if (!context) {
            throw new Error("Refund context unavailable.");
          }
          const previewData =
            context.preview ||
            (originalTicketId ? await getRefundPreview(originalTicketId) : null);
          if (!previewData) {
            throw new Error("Refund preview unavailable.");
          }
          setPreview(previewData);
          setReason(context.reason || "");
          setNote(context.note || "");
          const nextRefundMode = (context.refund_mode || "original").toLowerCase();
          setRefundMode(nextRefundMode);
          setGiftCardCode(context.gift_card_code || "");
          if (nextRefundMode === "gift_card") {
            setGiftCardSuggested(true);
          }
          setAllocationMode(
            nextRefundMode === "gift_card" ? "auto" : "manual"
          );

          const amounts = {};
          const lineAmountsById = {};
          (context.lines || []).forEach((line) => {
            lineAmountsById[line.original_ticket_item_id] = Number(
              line.refund_amount || 0
            );
          });
          (previewData?.lines || []).forEach((line) => {
            const amount = lineAmountsById[line.ticket_item_id] || 0;
            amounts[line.ticket_item_id] = amount ? format2(amount) : "0";
          });
          setLineAmounts(amounts);

          const allocationByKey = {};
          (context.allocations || []).forEach((allocation) => {
            const method = String(allocation.method || "").toLowerCase();
            const reference = (allocation.reference || "").trim();
            const key = `${method}:${reference}`;
            allocationByKey[key] = Number(allocation.amount || 0);
          });
          const manualAllocations = {};
          const manualDrafts = {};
          (previewData?.payments || []).forEach((payment) => {
            const key = getPaymentKey(payment);
            const amount = allocationByKey[key] || 0;
            manualAllocations[key] = amount;
            manualDrafts[key] = format2(amount);
          });
          setAllocationsByKey(manualAllocations);
          setAllocationDrafts(manualDrafts);
          setManualAllocationError("");
        } else {
          if (!ticketId) return;
          const data = await getRefundPreview(ticketId);
          if (!isActive) return;
          setPreview(data);
          const amounts = {};
          (data?.lines || []).forEach((line) => {
            amounts[line.ticket_item_id] = "0";
          });
          setLineAmounts(amounts);
          const manualAllocations = {};
          const manualDrafts = {};
          (data?.payments || []).forEach((payment) => {
            const key = getPaymentKey(payment);
            manualAllocations[key] = 0;
            manualDrafts[key] = "0.00";
          });
          setAllocationsByKey(manualAllocations);
          setAllocationDrafts(manualDrafts);
        }
      } catch (err) {
        if (!isActive) return;
        setError(
          err?.response?.data?.detail || err?.message || "Unable to load refund preview."
        );
      } finally {
        if (!isActive) return;
        setLoading(false);
      }
    };

    loadRefundData();

    return () => {
      isActive = false;
    };
  }, [isOpen, ticketId, mode, originalTicketId, refundTicketId, initialContext]);

  useEffect(() => {
    if (!isOpen || refundMode !== "gift_card" || giftCardSuggested) return;
    let isActive = true;
    const setSuggestedCode = async () => {
      try {
        const data = await generateGiftCardCode();
        if (!isActive) return;
        const suggested = String(data?.code || "").trim();
        setGiftCardCode(suggested || generateLocalGiftCardCode());
      } catch (err) {
        if (!isActive) return;
        setGiftCardCode(generateLocalGiftCardCode());
      } finally {
        if (isActive) setGiftCardSuggested(true);
      }
    };
    setSuggestedCode();
    return () => {
      isActive = false;
    };
  }, [giftCardSuggested, isOpen, refundMode]);

  const lines = preview?.lines || [];
  const serviceLines = useMemo(() => {
    return lines.filter(
      (line) => String(line?.line_type || "service").toLowerCase() === "service"
    );
  }, [lines]);
  const giftCardLines = useMemo(() => {
    return lines.filter(
      (line) => String(line?.line_type || "").toLowerCase() === "gift_card"
    );
  }, [lines]);
  const payments = useMemo(() => {
    return (preview?.payments || []).map((payment) => ({
      ...payment,
      key: getPaymentKey(payment),
    }));
  }, [preview]);
  const paymentRemainingByKey = useMemo(() => {
    const map = {};
    payments.forEach((payment) => {
      map[payment.key] = Number(payment.remaining_amount || 0);
    });
    return map;
  }, [payments]);
  const remainingRefundable = Number(preview?.remaining_refundable || 0);
  const totalPaymentRemaining = payments.reduce((sum, payment) => {
    const remaining = Number(payment.remaining_amount || 0);
    return Number.isFinite(remaining) ? sum + remaining : sum;
  }, 0);

  const refundTotal = useMemo(() => {
    return lines.reduce((sum, line) => {
      const amount = parseAmount(lineAmounts[line.ticket_item_id]);
      if (amount > 0) return sum + amount;
      return sum;
    }, 0);
  }, [lines, lineAmounts]);

  const hasInvalidLine = useMemo(() => {
    return lines.some((line) => {
      const amount = parseAmount(lineAmounts[line.ticket_item_id]);
      const remaining = Number(line.remaining_amount || 0);
      return amount > remaining + 0.001;
    });
  }, [lines, lineAmounts]);

  useEffect(() => {
    if (refundMode !== "original") {
      setAutoAllocationError("");
      setManualAllocationError("");
      return;
    }
    if (allocationMode !== "auto") return;
    let remainingToAllocate = refundTotal;
    const allocations = {};
    payments.forEach((payment) => {
      const remaining = Number(payment.remaining_amount || 0);
      const take = Math.min(remaining, Math.max(remainingToAllocate, 0));
      allocations[payment.key] = take;
      remainingToAllocate = Math.max(0, remainingToAllocate - take);
    });
    setComputedAutoAllocations(allocations);
    if (remainingToAllocate > 0.009) {
      setAutoAllocationError(
        "Refund total exceeds refundable payment balance. Reduce refund amount."
      );
    } else {
      setAutoAllocationError("");
    }
  }, [allocationMode, payments, refundMode, refundTotal]);

  useEffect(() => {
    if (refundMode !== "original" || allocationMode !== "manual") return;
    if (!payments.length) return;

    const orderedKeys = payments.map((payment) => payment.key);

    const clampToRemaining = (key, value) => {
      const remaining = paymentRemainingByKey[key] ?? 0;
      const numeric = Number(value || 0);
      if (!Number.isFinite(numeric)) return 0;
      return Math.max(0, Math.min(round2(numeric), remaining));
    };

    const next = {};
    orderedKeys.forEach((key) => {
      next[key] = clampToRemaining(key, allocationsByKey[key]);
    });

    const sumNext = orderedKeys.reduce((sum, key) => sum + next[key], 0);

    if (sumNext > refundTotal + 0.009) {
      let over = sumNext - refundTotal;
      for (let i = orderedKeys.length - 1; i >= 0 && over > 0.009; i -= 1) {
        const key = orderedKeys[i];
        const reducible = Math.min(next[key], over);
        next[key] = round2(next[key] - reducible);
        over -= reducible;
      }
    }

    const changedKeys = [];
    orderedKeys.forEach((key) => {
      const prevVal = clampToRemaining(key, allocationsByKey[key]);
      if (Math.abs((next[key] || 0) - (prevVal || 0)) > 0.0009) {
        changedKeys.push(key);
      }
    });

    if (changedKeys.length === 0) return;

    setAllocationsByKey((prev) => {
      const updated = { ...prev };
      changedKeys.forEach((key) => {
        updated[key] = next[key];
      });
      return updated;
    });

    setAllocationDrafts((prev) => {
      const updated = { ...prev };
      changedKeys.forEach((key) => {
        if (key === focusedAllocationKey) return;
        updated[key] = format2(next[key]);
      });
      return updated;
    });

    setManualAllocationError("");
  }, [
    allocationMode,
    allocationsByKey,
    paymentRemainingByKey,
    payments,
    refundMode,
    refundTotal,
    focusedAllocationKey,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (giftCardLines.length > 0 && refundMode === "gift_card") {
      setRefundMode("original");
      setAllocationMode("auto");
    }
  }, [giftCardLines.length, isOpen, refundMode]);

  const allocationsSum = useMemo(() => {
    return payments.reduce((sum, payment) => {
      const amount = Number(allocationsByKey[payment.key] || 0);
      return Number.isFinite(amount) ? sum + amount : sum;
    }, 0);
  }, [allocationsByKey, payments]);

  const unallocatedAmount = useMemo(() => {
    const diff = refundTotal - allocationsSum;
    return Math.abs(diff) < 0.009 ? 0 : diff;
  }, [allocationsSum, refundTotal]);

  const needsNote = reason === "Other";
  const hasAllocationError =
    refundMode === "original" &&
    (totalPaymentRemaining <= 0 ||
      (allocationMode === "auto" && autoAllocationError) ||
      (allocationMode === "manual" && (manualAllocationError || unallocatedAmount !== 0)));
  const canSubmit =
    refundTotal > 0 &&
    refundTotal <= remainingRefundable + 0.001 &&
    !hasInvalidLine &&
    reason &&
    (!needsNote || note.trim()) &&
    !hasAllocationError &&
    !loading &&
    !submitting;

  const handleAmountChange = (ticketItemId, value) => {
    setLineAmounts((prev) => ({
      ...prev,
      [ticketItemId]: value,
    }));
  };

  const handleAllocationChange = (key, value) => {
    setAllocationDrafts((prev) => ({
      ...prev,
      [key]: sanitizeDecimalInput(value),
    }));
    setManualAllocationError("");
  };

  // Drafts stay editable; clamp/format only when the user leaves the field.
  const handleAllocationBlur = (key, rawValue) => {
    const payment = payments.find((row) => row.key === key);
    const remaining = Number(payment?.remaining_amount || 0);
    const parsed = parseMoneySafe(
      rawValue !== undefined ? rawValue : allocationDrafts[key]
    );
    let nextValue = parsed == null ? 0 : parsed;
    if (nextValue < 0) nextValue = 0;
    if (nextValue > remaining) nextValue = remaining;
    nextValue = round2(nextValue);
    setAllocationsByKey((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
    setAllocationDrafts((prev) => ({
      ...prev,
      [key]: format2(nextValue),
    }));
  };

  const handleGiftCardBlur = async () => {
    const code = giftCardCode.trim();
    if (!code) {
      setGiftCardWarning("");
      return;
    }
    setGiftCardChecking(true);
    try {
      const result = await checkGiftCardCodeAvailable(code);
      if (result?.exists) {
        setGiftCardWarning(
          "This code is already in use. A new code will be issued."
        );
      } else {
        setGiftCardWarning("");
      }
    } catch (err) {
      setGiftCardWarning("");
    } finally {
      setGiftCardChecking(false);
    }
  };

  const handleGenerateGiftCardCode = async () => {
    setGiftCardChecking(true);
    try {
      const data = await generateGiftCardCode();
      const suggested = String(data?.code || "").trim();
      setGiftCardCode(suggested || generateLocalGiftCardCode());
      setGiftCardWarning("");
    } catch (err) {
      setGiftCardCode(generateLocalGiftCardCode());
      setGiftCardWarning("");
    } finally {
      setGiftCardChecking(false);
      setGiftCardSuggested(true);
    }
  };

  const handleCopyIssuedCode = async () => {
    if (!issuedGiftCardCode) return;
    try {
      await navigator.clipboard.writeText(issuedGiftCardCode);
    } catch (err) {
      setError("Unable to copy the gift card code.");
    }
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    const targetTicketId =
      mode === "edit" ? refundTicketId : ticketId;
    if (!targetTicketId) return;
    setSubmitting(true);
    setError("");
    try {
      const linesPayload = lines
        .map((line) => ({
          ticket_item_id: line.ticket_item_id,
          amount: parseAmount(lineAmounts[line.ticket_item_id]),
        }))
        .filter((line) => line.amount > 0);
      const trimmedGiftCardCode = giftCardCode.trim();
      const payload = {
        refund_mode: refundMode,
        gift_card_code:
          refundMode === "gift_card" && trimmedGiftCardCode
            ? trimmedGiftCardCode
            : null,
        reason: reason,
        note: note.trim() ? note.trim() : null,
        lines: linesPayload,
      };
      if (refundMode === "original" && allocationMode === "manual") {
        const allocations = payments
          .map((payment) => ({
            method: payment.method,
            reference: payment.reference || null,
            amount: Number(allocationsByKey[payment.key] || 0),
          }))
          .filter((row) => Number.isFinite(row.amount) && row.amount > 0);
        payload.allocations = allocations;
      }
      const result =
        mode === "edit"
          ? await updateTicketRefund(targetTicketId, payload)
          : await createTicketRefund(targetTicketId, payload);
      const isGiftCardRefund = refundMode === "gift_card";
      if (mode === "edit") {
        onRefundUpdated?.(result);
        if (!isGiftCardRefund) {
          onClose?.();
        }
      } else {
        onRefundCreated?.(result);
        if (!isGiftCardRefund) {
          onClose?.();
        }
      }
      window.dispatchEvent(
        new CustomEvent("tickets:updated", {
          detail: {
            ticket_id: targetTicketId,
            refund_ticket_id: result?.refund_ticket_id,
          },
        })
      );
      if (isGiftCardRefund) {
        const issued = String(result?.gift_card_code || "").trim();
        setIssuedGiftCardCode(issued || trimmedGiftCardCode || "");
        setShowIssuedGiftCard(true);
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.detail || err?.message || "Unable to create refund."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const footer = showIssuedGiftCard ? null : (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={!canSubmit}
        className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
      >
        {submitting ? "Processing..." : "Confirm Refund"}
      </button>
    </div>
  );

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title="Refund Ticket"
      size="xxl"
      footer={footer}
    >
      <div className="refund-modal-body">
        {showIssuedGiftCard ? (
          <div className="refund-issued space-y-4">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                Gift card issued
              </div>
              <div className="mt-2 text-lg font-semibold text-emerald-900">
                Gift card issued: {issuedGiftCardCode || "Pending code"}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={handleCopyIssuedCode}
                disabled={!issuedGiftCardCode}
                className="rounded-full border border-emerald-300 px-5 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="refund-form space-y-6">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-sm text-slate-500">Loading preview...</div>
            )}

            {!loading && preview && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-2 text-sm text-slate-700 md:grid-cols-2">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-500">
                        Ticket
                      </div>
                      <div className="text-base font-semibold text-slate-900">
                        #{preview.ticket_number}
                      </div>
                      <div className="text-sm text-slate-600">
                        {preview.client_name || "Walk-in"}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-slate-700">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Original total</span>
                        <span className="font-medium">
                          {formatMoney(preview.original_grand_total)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Already refunded</span>
                        <span className="font-medium">
                          {formatMoney(preview.total_refunded)}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-semibold text-slate-900">
                        <span>Remaining refundable</span>
                        <span>{formatMoney(preview.remaining_refundable)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Refundable Services
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-slate-200">
                    <div className="grid grid-cols-[1.4fr,0.8fr,0.7fr,0.7fr,0.7fr,0.9fr] gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>Service</span>
                      <span>Staff</span>
                      <span className="text-right">Original</span>
                      <span className="text-right">Refunded</span>
                      <span className="text-right">Remaining</span>
                      <span className="text-right">Refund Amount</span>
                    </div>
                    <div className="divide-y divide-slate-200">
                      {serviceLines.map((line) => {
                        const remaining = Number(line.remaining_amount || 0);
                        const amountValue = lineAmounts[line.ticket_item_id] ?? "0";
                        const amountNum = parseAmount(amountValue);
                        const overLimit = amountNum > remaining + 0.001;
                        return (
                          <div
                            key={line.ticket_item_id}
                            className="grid grid-cols-[1.4fr,0.8fr,0.7fr,0.7fr,0.7fr,0.9fr] gap-2 px-4 py-3 text-sm text-slate-700"
                          >
                            <span className="font-semibold text-slate-900">
                              {line.description}
                            </span>
                            <span>{line.staff_name || "--"}</span>
                            <span className="text-right">
                              {formatMoney(line.original_amount)}
                            </span>
                            <span className="text-right">
                              {formatMoney(line.refunded_amount)}
                            </span>
                            <span className="text-right">
                              {formatMoney(line.remaining_amount)}
                            </span>
                            <span className="text-right">
                              <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                value={amountValue}
                                onChange={(event) =>
                                  handleAmountChange(
                                    line.ticket_item_id,
                                    event.target.value
                                  )
                                }
                                className={`w-24 rounded-xl border px-2 py-1 text-right ${
                                  overLimit
                                    ? "border-red-300 text-red-600"
                                    : "border-slate-300"
                                }`}
                              />
                            </span>
                          </div>
                        );
                      })}
                      {serviceLines.length === 0 && giftCardLines.length === 0 && (
                        <div className="px-4 py-4 text-sm text-slate-500">
                          No refundable items available.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {giftCardLines.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Refundable Gift Cards
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-slate-200">
                      <div className="grid grid-cols-[1.2fr,0.7fr,0.7fr,0.7fr,0.9fr] gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <span>Gift Card</span>
                        <span className="text-right">Original</span>
                        <span className="text-right">Refunded</span>
                        <span className="text-right">Remaining</span>
                        <span className="text-right">Refund Amount</span>
                      </div>
                      <div className="divide-y divide-slate-200">
                        {giftCardLines.map((line) => {
                          const remaining = Number(line.remaining_amount || 0);
                          const amountValue = lineAmounts[line.ticket_item_id] ?? "0";
                          const amountNum = parseAmount(amountValue);
                          const overLimit = amountNum > remaining + 0.001;
                          return (
                            <div
                              key={line.ticket_item_id}
                              className="grid grid-cols-[1.2fr,0.7fr,0.7fr,0.7fr,0.9fr] gap-2 px-4 py-3 text-sm text-slate-700"
                            >
                              <span className="font-semibold text-slate-900">
                                {getGiftCardLabel(line)}
                              </span>
                              <span className="text-right">
                                {formatMoney(line.original_amount)}
                              </span>
                              <span className="text-right">
                                {formatMoney(line.refunded_amount)}
                              </span>
                              <span className="text-right">
                                {formatMoney(line.remaining_amount)}
                              </span>
                              <span className="text-right">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  step="0.01"
                                  value={amountValue}
                                  onChange={(event) =>
                                    handleAmountChange(
                                      line.ticket_item_id,
                                      event.target.value
                                    )
                                  }
                                  className={`w-24 rounded-xl border px-2 py-1 text-right ${
                                    overLimit
                                      ? "border-red-300 text-red-600"
                                      : "border-slate-300"
                                  }`}
                                />
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Reason
                    </div>
                    <select
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">Select a reason</option>
                      {REASON_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Note {needsNote ? "(required)" : "(optional)"}
                      </div>
                      <textarea
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        rows={3}
                        className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                        placeholder="Add a note..."
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Refund method
                    </div>
                    <div className="space-y-2 text-sm text-slate-700">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="refund_mode"
                          value="original"
                          checked={refundMode === "original"}
                          onChange={() => setRefundMode("original")}
                        />
                        Refund to original payment method(s)
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="refund_mode"
                          value="gift_card"
                          checked={refundMode === "gift_card"}
                          onChange={() => setRefundMode("gift_card")}
                          disabled={giftCardLines.length > 0}
                        />
                        Refund to gift card (store credit)
                      </label>
                      {giftCardLines.length > 0 && (
                        <div className="text-xs text-slate-500">
                          Gift card purchases must be refunded to the original payment
                          method(s).
                        </div>
                      )}
                    </div>

                    {refundMode === "gift_card" && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Gift card code
                        </div>
                        <input
                          type="text"
                          value={giftCardCode}
                          onChange={(event) => {
                            setGiftCardCode(event.target.value);
                            setGiftCardWarning("");
                            setGiftCardSuggested(true);
                          }}
                          onBlur={handleGiftCardBlur}
                          className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Enter gift card code"
                        />
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                          <span>Leave blank to auto-generate a new code.</span>
                          <button
                            type="button"
                            onClick={handleGenerateGiftCardCode}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                            disabled={giftCardChecking}
                          >
                            {giftCardChecking ? "Working..." : "Generate new code"}
                          </button>
                        </div>
                        {giftCardWarning && (
                          <div className="mt-2 text-xs font-semibold text-amber-600">
                            {giftCardWarning}
                          </div>
                        )}
                      </div>
                    )}

                    {refundMode === "original" && (
                      <div className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Allocation
                        </div>
                        <div className="space-y-2 text-sm text-slate-700">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="allocation_mode"
                              value="auto"
                              checked={allocationMode === "auto"}
                              onChange={() => setAllocationMode("auto")}
                            />
                            Auto allocate (original payment order)
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="allocation_mode"
                              value="manual"
                              checked={allocationMode === "manual"}
                              onChange={() => setAllocationMode("manual")}
                            />
                            Choose allocation (manual)
                          </label>
                        </div>

                        {allocationMode === "auto" && (
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Will be refunded to
                            </div>
                            <div className="mt-2 space-y-1">
                              {payments.map((payment) => {
                                const allocated = Number(
                                  computedAutoAllocations[payment.key] || 0
                                );
                                const remaining =
                                  Number(payment.remaining_amount || 0) - allocated;
                                return (
                                  <div
                                    key={payment.key}
                                    className="flex justify-between"
                                  >
                                    <span>
                                      {formatMethodLabel(payment.method)}
                                      {payment.reference
                                        ? ` (${payment.reference})`
                                        : ""}
                                    </span>
                                    <span>
                                      {formatMoney(allocated)} ->{" "}
                                      {formatMoney(Math.max(remaining, 0))} remaining
                                    </span>
                                  </div>
                                );
                              })}
                              {payments.length === 0 && (
                                <div className="text-xs text-slate-500">
                                  No refundable original payments found.
                                </div>
                              )}
                            </div>
                            <div className="mt-2 text-xs text-slate-500">
                              Allocations follow original payment order.
                            </div>
                            {autoAllocationError && (
                              <div className="mt-2 text-xs text-red-600">
                                {autoAllocationError}
                              </div>
                            )}
                          </div>
                        )}

                        {allocationMode === "manual" && (
                          <div className="space-y-2">
                            <div className="overflow-hidden rounded-2xl border border-slate-200">
                              <div className="grid grid-cols-[1.1fr,0.8fr,0.8fr,0.9fr] gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <span>Method</span>
                                <span className="text-right">Remaining</span>
                                <span className="text-right">Allocate</span>
                                <span className="text-right">Balance</span>
                              </div>
                              <div className="divide-y divide-slate-200">
                                {payments.map((payment) => {
                                  const paymentKey = payment.key;
                                  const remaining = Number(
                                    payment.remaining_amount || 0
                                  );
                                  const allocated = Number(
                                    allocationsByKey[paymentKey] || 0
                                  );
                                  const balance = remaining - allocated;
                                  const label =
                                    payment.method === "gift_card" &&
                                    payment.reference
                                      ? `Gift Card (${payment.reference})`
                                      : formatMethodLabel(payment.method);
                                  const draftValue =
                                    allocationDrafts[paymentKey] ??
                                    format2(allocationsByKey[paymentKey] || 0);
                                  return (
                                    <div
                                      key={paymentKey}
                                      className="grid grid-cols-[1.1fr,0.8fr,0.8fr,0.9fr] gap-2 px-4 py-3 text-sm text-slate-700"
                                    >
                                      <span>{label}</span>
                                      <span className="text-right">
                                        {formatMoney(remaining)}
                                      </span>
                                      <span className="text-right">
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          autoComplete="off"
                                          spellCheck={false}
                                          value={draftValue}
                                          onFocus={(event) => {
                                            setFocusedAllocationKey(paymentKey);
                                            const current = String(
                                              draftValue ?? ""
                                            ).trim();
                                            if (
                                              current === "0.00" ||
                                              current === "0" ||
                                              current === "0."
                                            ) {
                                              setAllocationDrafts((prev) => ({
                                                ...prev,
                                                [paymentKey]: "",
                                              }));
                                              setTimeout(() => {
                                                try {
                                                  event.target.select();
                                                } catch (err) {
                                                  // No-op for selection errors.
                                                }
                                              }, 0);
                                              return;
                                            }
                                            setTimeout(() => {
                                              try {
                                                event.target.select();
                                              } catch (err) {
                                                // No-op for selection errors.
                                              }
                                            }, 0);
                                          }}
                                          onChange={(event) =>
                                            handleAllocationChange(
                                              paymentKey,
                                              event.target.value
                                            )
                                          }
                                          onBlur={(event) => {
                                            handleAllocationBlur(
                                              paymentKey,
                                              event.target.value
                                            );
                                            setFocusedAllocationKey(null);
                                          }}
                                          className="w-24 rounded-xl border border-slate-300 px-2 py-1 text-right"
                                        />
                                      </span>
                                      <span className="text-right">
                                        {formatMoney(Math.max(balance, 0))}
                                      </span>
                                    </div>
                                  );
                                })}
                                {payments.length === 0 && (
                                  <div className="px-4 py-4 text-sm text-slate-500">
                                    No refundable original payments found.
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-sm text-slate-700">
                              <span className="text-slate-500">Unallocated</span>
                              <span
                                className={`font-semibold ${
                                  unallocatedAmount === 0
                                    ? "text-slate-900"
                                    : "text-red-600"
                                }`}
                              >
                                {formatMoney(unallocatedAmount)}
                              </span>
                            </div>
                            {manualAllocationError && (
                              <div className="text-xs text-red-600">
                                {manualAllocationError}
                              </div>
                            )}
                            {refundTotal > totalPaymentRemaining + 0.009 && (
                              <div className="text-xs text-red-600">
                                Refund total exceeds refundable payment balance. Reduce refund
                                amount.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <span className="text-sm text-slate-500">Refund total</span>
                  <span className="text-lg font-semibold text-slate-900">
                    {formatMoney(refundTotal)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SBINModal>
  );
}

function generateLocalGiftCardCode() {
  const value = Math.floor(10000 + Math.random() * 90000);
  return String(value);
}

function sanitizeDecimalInput(raw) {
  const value = String(raw ?? "");

  // keep digits and dots only
  const cleaned = value.replace(/[^\d.]/g, "");

  // allow empty
  if (cleaned === "") return "";

  // if multiple dots, keep first dot only
  const firstDot = cleaned.indexOf(".");
  if (firstDot === -1) {
    // no dot, just digits
    return cleaned;
  }

  const intPart = cleaned.slice(0, firstDot);
  const rest = cleaned.slice(firstDot + 1).replace(/\./g, "");
  const fracPart = rest.slice(0, 2);

  // allow "." as first input
  if (intPart === "" && cleaned.startsWith(".")) {
    return fracPart.length ? `.${fracPart}` : ".";
  }

  return `${intPart}.${fracPart}`;
}

function parseMoneySafe(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === ".") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function format2(value) {
  return Number(value || 0).toFixed(2);
}

RefundTicketModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mode: PropTypes.oneOf(["create", "edit"]),
  originalTicketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refundTicketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialContext: PropTypes.shape({
    refund_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refund_ticket_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    original_ticket_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refund_mode: PropTypes.string,
    gift_card_code: PropTypes.string,
    reason: PropTypes.string,
    note: PropTypes.string,
    allocation_mode: PropTypes.string,
    lines: PropTypes.arrayOf(
      PropTypes.shape({
        original_ticket_item_id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        refund_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    allocations: PropTypes.arrayOf(
      PropTypes.shape({
        method: PropTypes.string,
        reference: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    preview: PropTypes.shape({
      original_ticket_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ticket_number: PropTypes.string,
      status: PropTypes.string,
      client_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      client_name: PropTypes.string,
      original_grand_total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_refunded: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      remaining_refundable: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lines: PropTypes.array,
      payments: PropTypes.array,
    }),
  }),
  onClose: PropTypes.func.isRequired,
  onRefundCreated: PropTypes.func,
  onRefundUpdated: PropTypes.func,
};
